import Router from './routes'
import ThemeConfig from './theme'
import { useAuth } from './hooks/useAuth'
import CircularProgress from '@mui/material/CircularProgress'
import { ReactElement, useContext, useEffect, useMemo, useRef } from 'react'
import { Request, REQUEST_MESSAGE_TYPES } from 'schemas/request'
import { generateID } from 'utils/generateMessageID'
import NotistackProvider from './components/NotistackProvider'
import { ConfigContext } from './contexts/ConfigurationContext'
import { setAuthenticated } from 'redux/slices/socket-auth-status'
import { useDispatch } from './redux/store'
import { Box } from '@mui/material'
import { QueryContractCriteriaEnum } from '@types'
import useIdleTimeout from 'hooks/useIdleTimeout'
import { clearAllReduxCaches } from 'pages/dashboard/utils'
import { useLocation, useNavigate } from 'react-router-dom'
import { useSocketConnection } from './hooks/useSocketConnection'
import { useAuthHook } from 'hooks/useAuthHook'

export default function App() {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { logout } = useAuth()
  const config = useContext(ConfigContext)
  const isAuthenticated = useAuthHook()
  const ranOnce = useRef(false)

  const { onSocketSend, waitForAuthentication } = useSocketConnection()

  const idleTimeout = useMemo(() => {
    const minutes = parseInt(config?.REACT_APP_IDLE_TIMEOUT || '0')
    return minutes * 60 * 1000
  }, [config?.REACT_APP_IDLE_TIMEOUT])

  const warningDuration = useMemo(() => {
    const minutes = parseInt(config?.REACT_APP_WARNING_DURATION || '0')
    return minutes * 60 * 1000
  }, [config?.REACT_APP_WARNING_DURATION])

  const handleLogout = async () => {
    logout()
    navigate('/auth/login')
    clearAllReduxCaches(dispatch)
  }

  const { WarningPopup } = useIdleTimeout({
    timeout: idleTimeout,
    warningTime: warningDuration,
    onIdle: () => console.log('Application:: User is Idle'),
    onLogout: handleLogout
  })

  const triggerInitialSocketCalls = async () => {
    const requests: Request[] = [
      {
        messageId: generateID(),
        messageType: REQUEST_MESSAGE_TYPES.QueryContract,
        QueryContract: { criteria: QueryContractCriteriaEnum.lastActioned, limit: 100 }
      },
      {
        messageId: generateID(),
        messageType: REQUEST_MESSAGE_TYPES.QueryCreateActions,
        QueryCreateActions: {}
      },
      {
        messageId: generateID(),
        messageType: REQUEST_MESSAGE_TYPES.QueryNotifications,
        QueryNotifications: {
          isAcknowledged: false,
          limit: 1000
        }
      },
      {
        messageId: generateID(),
        messageType: REQUEST_MESSAGE_TYPES.QueryPurposeTypes,
        QueryPurposeTypes: {}
      }
    ]

    for (const req of requests) {
      const success = await onSocketSend(req)
      if (!success) {
        console.warn('❌ Application:: Failed to send request:', req.messageType)
        break
      }
    }
  }

  // 🔐 Register auth callback exactly once
  useEffect(() => {
    (async () => {
      await waitForAuthentication()
      if (ranOnce.current) return
      ranOnce.current = true
      dispatch(setAuthenticated(true))
      triggerInitialSocketCalls()
    })()
  }, [waitForAuthentication, dispatch])

  // 🔁 Scroll restoration
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [pathname])

  return (
    <ThemeConfig>
      <NotistackProvider>
        {isAuthenticated ? (
          <>
            <WarningPopup />
            <Router />
          </>
        ) : (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
            <CircularProgress />
          </Box>
        )}
      </NotistackProvider>
    </ThemeConfig>
  )
}
